import { Link } from "react-router";
import { Alerta, Badge, BarraSaldo, Button, DataList, MetricCard, Spinner, Stack, type ColumnaDataList } from "@/components";
import type { Cuenta } from "@/lib/api";
import { cn } from "@/lib/cn";
import {
  COLOR_SALDO,
  cuantosFiduciantes,
  ETIQUETA_SALDO,
  estadoSaldo,
  formatSaldo,
  proporcionSaldo,
  resumenSaldos,
  saldoContable,
  TONO_SALDO,
} from "@/lib/cuentas";
import { formatMoneda } from "@/lib/format";
import { parseMonto } from "@/lib/gastos";
import { useCuentasCorrientesTab } from "./useCuentasCorrientesTab";

const claseLink = "text-primario underline-offset-2 hover:underline";


function columnas(saldos: string[]): ColumnaDataList<Cuenta>[] {
  return [
    {
      clave: "nombre",
      titulo: "Fiduciante",
      render: (c) => (
        <Stack espacio={1}>
          <Link to={`/cuentas-corrientes/${c.fiduciante_id}`} className={cn(claseLink, "font-medium")}>
            {c.nombre}
          </Link>
          {parseMonto(c.vencido_usd) !== null && <span className="text-chico text-peligro">Previsión vencida: {formatMoneda(c.vencido_usd, "USD")}</span>}
        </Stack>
      ),
    },
    {
      clave: "estado",
      titulo: "Estado",
      render: (c) => {
        const estado = estadoSaldo(c.saldo_usd);
        return <Badge tono={TONO_SALDO[estado]}>{ETIQUETA_SALDO[estado]}</Badge>;
      },
    },
    {
      clave: "saldo",
      titulo: "Saldo",
      render: (c) => {
        const estado = estadoSaldo(c.saldo_usd);
        return (
          <div className="grid min-w-48 gap-1">
            <span className={cn("font-semibold tabular-nums", COLOR_SALDO[estado])}>{saldoContable(c.saldo_usd)}</span>
            <BarraSaldo proporcion={proporcionSaldo(c.saldo_usd, saldos)} etiqueta={`Saldo de ${c.nombre}: ${ETIQUETA_SALDO[estado].toLowerCase()} ${formatSaldo(c.saldo_usd)}`} />
          </div>
        );
      },
    },
  ];
}

export function CuentasCorrientesTab() {
  const t = useCuentasCorrientesTab();
  const tot = t.cuentas?.totales;
  const items = t.cuentas?.items ?? [];
  const resumen = resumenSaldos(items);
  const saldos = items.map((c) => c.saldo_usd);
  return (
    <Stack espacio={6}>
      <Stack espacio={1}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Cuentas corrientes</h1>
        <p className="text-chico text-texto-suave">Verde: tiene plata a favor. Rojo: debe. La barra compara a cada uno con el saldo más grande. Tocá un nombre para ver sus movimientos.</p>
      </Stack>
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>Reintentar</Button>
          </div>
        </Stack>
      )}
      {t.cuentas && tot && (
        <>
          {t.prevision && <MetricCard titulo="Previsión: queda" tono={t.prevision.tono} valor={t.prevision.valor} detalle={t.prevision.detalle} />}
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard titulo="A favor" tono="exito" valor={formatSaldo(resumen.a_favor_usd)} detalle={cuantosFiduciantes(resumen.cuantos_a_favor)} />
            <MetricCard titulo="Deben" tono={resumen.cuantos_deben > 0 ? "peligro" : undefined} valor={formatSaldo(resumen.deben_usd)} detalle={cuantosFiduciantes(resumen.cuantos_deben)} />
            <MetricCard titulo="Al día" valor={String(resumen.cuantos_al_dia)} detalle={resumen.cuantos_al_dia === 1 ? "fiduciante en cero" : "fiduciantes en cero"} />
          </div>
          <Alerta tono="alerta" texto={t.avisoSinRepartir} />
          <DataList etiqueta="Cuentas corrientes" columnas={columnas(saldos)} filas={t.cuentas.items} claveFila={(c) => c.fiduciante_id} vacio="Todavía no hay fiduciantes." />
        </>
      )}
    </Stack>
  );
}
